'use client';

import { MilestoneCarousel } from '@wwe-portal/ui/features/store-revamp/milestone/milestone-carousel';
import {
  EventData,
  Milestone,
  UserEventProgress,
} from '@wwe-portal/entities/rbe.type';

import {
  OfferReviewDialog,
  OfferReviewDialogProps,
  OfferReviewProps,
} from '@wwe-portal/ui/features/offer/offer-review';
import {
  formatQuantity,
  getDescriptionItem,
} from '@wwe-portal/web/utils/offer';

import {
  createPortableDialog,
  showDialog,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog/portable-dialog';
import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { useRouter } from 'next/navigation';
import { useGetUserSoloEvents } from '@wwe-portal/web/providers/queries/rbe';

const mapMileStone = (
  event: EventData,
  progress: UserEventProgress | undefined
) => {
  const currentPoint = progress?.point ?? 0;
  const notReadyMilestone = event.milestones.find(
    (item) => currentPoint < item.requirement
  );
  const lastMilestone = event.milestones[event.milestones.length - 1];
  const isLatestMilestone =
    currentPoint && lastMilestone && lastMilestone.requirement <= currentPoint;

  const currentMilestone = isLatestMilestone
    ? lastMilestone
    : (notReadyMilestone ?? event.milestones[0]);

  const currentLevel = isLatestMilestone
    ? lastMilestone.level
    : (notReadyMilestone?.level ?? event.milestones[0]?.level);

  const totalProgress = currentMilestone?.requirement ?? 0;

  const currentProgress =
    currentPoint > totalProgress ? totalProgress : currentPoint;

  return {
    eventId: event.eventId,
    milestoneId: currentMilestone?.id ?? '',
    title: event.name,
    totalProgress,
    currentProgress,
    level: currentLevel,
    totalRewards: currentMilestone?.rewards.length,
    endDate: event.endDate,
    isEnded: new Date(event.endDate).getTime() < new Date().getTime(),
    pointRules: event.pointRules,
  };
};

const OfferPreviewPortable = createPortableDialog(
  ({ data }: { data: OfferReviewDialogProps['data'] }) => {
    const modal = usePortableDialog();
    return (
      <OfferReviewDialog
        onClose={modal.remove}
        open={modal.visible}
        data={{ ...data }}
      />
    );
  }
);

export const EventProgressionSection = () => {
  const { userProgressMap, soloEvents } = useGetUserSoloEvents();
  const router = useRouter();
  const events = soloEvents
    ?.map((item) => mapMileStone(item, userProgressMap.get(item.portalEventId)))
    .filter(({ isEnded }) => !isEnded);

  const parseMilestonePreview = (milestone: Milestone, endDate?: string) => {
    const result: OfferReviewProps = {
      id: milestone.id,
      title: 'MILESTONE REWARDS',
      items:
        milestone?.rewards?.map((reward) => {
          const rewardImages = JSON.parse(reward.imageUrl);
          return {
            defaultImageUrl: rewardImages.url,
            dynamicImageUrl: rewardImages.url,
            itemId: reward.itemId,
            text: formatQuantity(reward.quantity),
            description: getDescriptionItem(
              reward.quantity,
              reward.displayName
            ),
          };
        }) ?? [],
      rewardItems: [],
      rewards: [],
      endDate: endDate ?? '',
      vipPoints: undefined,
      disabled: false,
      storeItemDescription: '',
      actionBtn: null,
    };

    return result;
  };

  const handleClickDetails = (eventId: string, milestoneId: string) => {
    const event = soloEvents?.find((item) => item.eventId === eventId);
    const milestone = event?.milestones.find((item) => item.id === milestoneId);

    if (!milestone) return;
    showDialog(OfferPreviewPortable, {
      data: parseMilestonePreview(milestone, event?.endDate),
    });
  };

  if (!events || events.length === 0) return null;

  return (
    <ClickableDiv onClick={() => router.push('/limited-time-events')}>
      <div className="-mx-5 -mt-4 overflow-hidden pt-4 xl:mx-[-30px]">
        <div className="px-5 xl:px-[30px]">
          <MilestoneCarousel
            milestones={events}
            onClickDetails={handleClickDetails}
          />
        </div>
      </div>
    </ClickableDiv>
  );
};
